import React from 'react';
import { Button, Typography, Row, Col } from 'antd';
import {
    RocketOutlined,
    PlayCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { renderToString } from 'react-dom/server';
import { PiGlobeHemisphereEastBold, PiMapTrifold } from 'react-icons/pi';
import Routes from '~/routes/routes';
import { LandingPageUtil } from './LandingPageUtil';

const { Title, Paragraph } = Typography;

const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true },
};

interface HeroSectionProps {
    onNavigateToProjects?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onNavigateToProjects }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleContinue = () => {
        if (onNavigateToProjects) {
            onNavigateToProjects();
        } else {
            navigate(Routes.PROJECT);
        }
    };

    return (
        <div style={{ position: 'relative', zIndex: 1, padding: '60px 20px 60px' }}>
            <Row align="middle" style={{ maxWidth: 1200, margin: '0 auto' }}>
                <Col xs={24} md={12}>
                    <motion.div {...fadeUp}>
                        <span
                            style={{
                                display: 'inline-block',
                                marginBottom: 16,
                                padding: '4px 12px',
                                borderRadius: 999,
                                background: 'rgba(255,255,255,0.12)',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                            }}
                        >
                            <PiGlobeHemisphereEastBold style={{ verticalAlign: '-2px', marginRight: 6 }} /> GeoCloud
                        </span>

                        {/* Title theo mẫu felt.com */}
                        <Title
                            level={1}
                            style={{
                                fontSize: '2rem',
                                lineHeight: 1.2,
                                marginBottom: 16,
                                background: 'linear-gradient(135deg, #ffffff 0%, #b4b4b4 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            {t('landing__hero_title')}
                        </Title>

                        {/* Description theo mẫu felt.com */}
                        <Paragraph
                            style={{
                                fontSize: '1rem',
                                color: 'rgba(255,255,255,0.85)',
                                maxWidth: 540,
                                marginBottom: 24,
                                lineHeight: 1.6,
                            }}
                        >
                            {t('landing__hero_description')}
                        </Paragraph>

                        {/* Actions: Tiếp tục & Xem hướng dẫn */}
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <Button
                                type="primary"
                                size="large"
                                icon={<RocketOutlined />}
                                style={{
                                    height: 44,
                                    padding: '0 24px',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    borderRadius: 8,
                                    background: 'linear-gradient(135deg, #69c0ff, #d3adf7)',
                                    border: 'none',
                                    color: '#000',
                                }}
                                onClick={handleContinue}
                            >
                                {t('landing__btn_continue')}
                            </Button>

                            <Button
                                size="large"
                                icon={<PlayCircleOutlined />}
                                style={{
                                    height: 44,
                                    padding: '0 24px',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    borderRadius: 8,
                                    background: 'transparent',
                                    border: '2px solid rgba(255,255,255,0.3)',
                                    color: '#fff',
                                }}
                                onClick={LandingPageUtil.openDocumentation}
                            >
                                {t('landing__btn_watch_guide')}
                            </Button>
                        </div>
                    </motion.div>
                </Col>

                {/* Demo Image - Ảnh chụp màn hình ứng dụng */}
                <Col xs={24} md={12} style={{ marginTop: 40 }}>
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        viewport={{ once: true }}
                        style={{
                            borderRadius: 12,
                            overflow: 'hidden',
                            boxShadow: '0 15px 40px rgba(0,0,0,0.5)',
                            border: '1px solid rgba(255,255,255,0.15)',
                        }}
                    >
                        <img
                            src="/geocloud_screenshot.png"
                            alt="Geocloud Application Demo"
                            style={{
                                width: '100%',
                                display: 'block',
                            }}
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                    parent.style.background = 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)';
                                    parent.style.height = '250px';
                                    parent.style.display = 'flex';
                                    parent.style.alignItems = 'center';
                                    parent.style.justifyContent = 'center';
                                    const placeholder = document.createElement('div');
                                    placeholder.innerHTML = `<div style="color: rgba(255,255,255,0.5); font-size: 1rem; display: flex; align-items: center; gap: 8px;">${renderToString(<PiMapTrifold size={20} />)} Geocloud Application Demo</div>`;
                                    parent.appendChild(placeholder);
                                }
                            }}
                        />
                    </motion.div>
                </Col>
            </Row>
        </div>
    );
};

export default HeroSection;
